import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, MapPin, Calendar, User, MessageCircle, Image as ImageIcon, Phone, Globe, Clock, CreditCard, Users, Star, UtensilsCrossed, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import { getDishDisplayName } from '../../utils/dishDisplay';
import { useSnackbar } from '../../contexts/SnackbarContext';
import './RestaurantDetails.scss';

const RestaurantDetails = () => {
    const { restaurantID } = useParams();
    const navigate = useNavigate();
    const { success, error, confirm } = useSnackbar();
    const [restaurant, setRestaurant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deletingIndex, setDeletingIndex] = useState(null);

    useEffect(() => {
        fetchRestaurant();
    }, [restaurantID]);

    const handleDeleteImage = async (index) => {
        confirm('Delete this image?', async () => {
            setDeletingIndex(index);
            try {
                const response = await api.delete(`/admin/restaurants/${restaurantID}/images`, {
                    data: { imageIndex: index }
                });
                if (response.data.success) {
                    setRestaurant(prev => ({
                        ...prev,
                        images: prev.images.filter((_, i) => i !== index)
                    }));
                    success('Image deleted');
                }
            } catch (err) {
                error(err.response?.data?.message || 'Failed to delete image');
            } finally {
                setDeletingIndex(null);
            }
        });
    };

    const fetchRestaurant = async () => {
        try {
            const response = await api.get(`/admin/restaurants/${restaurantID}`);
            if (response.data.success) {
                setRestaurant(response.data.restaurant);
            }
        } catch (error) {
            console.error('Error fetching restaurant:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="loading">Loading restaurant details...</div>;
    }

    if (!restaurant) {
        return <div className="error">Restaurant not found</div>;
    }

    return (
        <div className="restaurant-details">
            <div className="details-header">
                <button className="back-button" onClick={() => navigate('/restaurants')}>
                    <ArrowLeft size={20} />
                    Back
                </button>
                <button className="edit-button" onClick={() => navigate(`/restaurants/${restaurantID}/edit`)}>
                    <Edit size={18} />
                    Edit
                </button>
            </div>

            <div className="details-content">
                <div className="section">
                    <div className="title-row">
                        <h2>{restaurant.name}</h2>
                        {restaurant.premium && (
                            <span className="premium-badge">
                                <Star size={16} fill="#fbbf24" color="#fbbf24" />
                                Premium
                            </span>
                        )}
                    </div>
                    <div className="info-grid">
                        <div className="info-item">
                            <strong>Restaurant ID:</strong> {restaurant.restaurantID}
                        </div>
                        <div className="info-item">
                            <strong>District:</strong> {restaurant.district}
                        </div>
                        <div className="info-item">
                            <strong>State:</strong> {restaurant.state}
                        </div>
                        <div className="info-item">
                            <strong>Dish:</strong> {getDishDisplayName(restaurant.dish)}
                        </div>
                        <div className="info-item">
                            <strong>Status:</strong>
                            <span className={`status-badge ${restaurant.status}`}>
                                {restaurant.status}
                            </span>
                        </div>
                        <div className="info-item">
                            <strong>Verified:</strong>
                            {restaurant.verified ? (
                                <span className="verified-badge">Yes</span>
                            ) : (
                                <span className="unverified-badge">No</span>
                            )}
                        </div>
                        {restaurant.googleRating && (
                            <div className="info-item">
                                <strong>Google Rating:</strong>
                                <span className="rating-display">
                                    <Star size={16} fill="#fbbf24" color="#fbbf24" />
                                    {restaurant.googleRating.toFixed(1)} ({restaurant.googleReviewCount} reviews)
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {restaurant.description && (
                    <div className="section">
                        <h3>Description</h3>
                        <p>{restaurant.description}</p>
                    </div>
                )}

                {restaurant.location && (
                    <div className="section">
                        <h3>Location</h3>
                        <div className="info-item">
                            <MapPin size={18} />
                            <a href={restaurant.googleMapsLink} target="_blank" rel="noopener noreferrer">
                                View on Google Maps
                            </a>
                        </div>
                        {restaurant.location.latitude && (
                            <div className="info-item">
                                <strong>Coordinates:</strong> {restaurant.location.latitude}, {restaurant.location.longitude}
                            </div>
                        )}
                    </div>
                )}

                {restaurant.contact && (restaurant.contact.phone || restaurant.contact.email || restaurant.contact.website) && (
                    <div className="section">
                        <h3>Contact Information</h3>
                        {restaurant.contact.phone && (
                            <div className="info-item">
                                <Phone size={18} />
                                <strong>Phone:</strong> {restaurant.contact.phone}
                            </div>
                        )}
                        {restaurant.contact.email && (
                            <div className="info-item">
                                <strong>Email:</strong> {restaurant.contact.email}
                            </div>
                        )}
                        {restaurant.contact.website && (
                            <div className="info-item">
                                <Globe size={18} />
                                <a href={restaurant.contact.website} target="_blank" rel="noopener noreferrer">
                                    {restaurant.contact.website}
                                </a>
                            </div>
                        )}
                    </div>
                )}

                {restaurant.operatingHours && (restaurant.operatingHours.start || restaurant.operatingHours.end || (Array.isArray(restaurant.operatingHours.days) && restaurant.operatingHours.days.length > 0)) && (
                    <div className="section">
                        <h3>
                            <Clock size={20} />
                            Operating Hours
                        </h3>
                        {restaurant.operatingHours.start && restaurant.operatingHours.end && (
                            <div className="info-item">
                                <strong>Hours:</strong> {restaurant.operatingHours.start} - {restaurant.operatingHours.end}
                            </div>
                        )}
                        {Array.isArray(restaurant.operatingHours.days) && restaurant.operatingHours.days.length > 0 && (
                            <div className="info-item">
                                <strong>Days:</strong> {restaurant.operatingHours.days.join(', ')}
                            </div>
                        )}
                    </div>
                )}

                {restaurant.paymentMethods && restaurant.paymentMethods.length > 0 && (
                    <div className="section">
                        <h3>
                            <CreditCard size={20} />
                            Payment Methods
                        </h3>
                        <div className="tags-list">
                            {restaurant.paymentMethods.map((method, index) => (
                                <span key={index} className="tag">{method}</span>
                            ))}
                        </div>
                    </div>
                )}

                {(restaurant.namazFacility || restaurant.womenFacility) && (
                    <div className="section">
                        <h3>Facilities</h3>
                        <div className="info-grid">
                            {restaurant.namazFacility && (
                                <div className="info-item">
                                    <strong>🤲 Namaz Facility:</strong> Available
                                </div>
                            )}
                            {restaurant.womenFacility && (
                                <div className="info-item">
                                    <strong>🧕 Women Facility:</strong> Available
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {restaurant.addedBy && (
                    <div className="section">
                        <h3>Added By</h3>
                        <div className="contributor-card">
                            <div>
                                <div className="info-item">
                                    <User size={18} />
                                    <strong>Name:</strong> {restaurant.addedBy.name}
                                </div>
                                <div className="info-item">
                                    <strong>Email:</strong> {restaurant.addedBy.email}
                                </div>
                                <div className="info-item">
                                    <Calendar size={18} />
                                    <strong>Added:</strong> {new Date(restaurant.addedBy.date).toLocaleDateString()}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {restaurant.comments && restaurant.comments.length > 0 && (
                    <div className="section">
                        <h3>
                            <MessageCircle size={20} />
                            Comments ({restaurant.comments.length})
                        </h3>
                        <div className="comments-list">
                            {restaurant.comments.map((comment, index) => (
                                <div key={index} className="comment-card">
                                    <div className="comment-header">
                                        {comment.picture && (
                                            <img src={comment.picture} alt={comment.name} />
                                        )}
                                        <div>
                                            <strong>{comment.name}</strong>
                                            <span>{new Date(comment.date).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                    <p>{comment.content}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {restaurant.images && restaurant.images.length > 0 && (
                    <div className="section">
                        <h3>
                            <ImageIcon size={20} />
                            Images ({restaurant.images.length})
                        </h3>
                        <div className="images-grid">
                            {restaurant.images.map((image, index) => (
                                <div key={index} className="image-item-with-delete">
                                    <img src={image.urlSmall || image.url} alt={`${restaurant.name} ${index + 1}`} />
                                    <button
                                        type="button"
                                        className="delete-image-btn"
                                        onClick={() => handleDeleteImage(index)}
                                        disabled={deletingIndex === index}
                                        title="Delete image"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {restaurant.keywords && restaurant.keywords.length > 0 && (
                    <div className="section">
                        <h3>Keywords</h3>
                        <div className="tags-list">
                            {restaurant.keywords.map((keyword, index) => (
                                <span key={index} className="tag">{keyword}</span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RestaurantDetails;
